import { useEffect, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router-dom'
import { isAfter, isBefore, parseISO } from 'date-fns'
import { getAllEventList } from '../../api/home'
import { PageTitle } from '../../components/title/PageTitle'
import '../../css/Exhibition.css'
import { AFWebLinkType, RNPostMessage } from '../../js/postMessage'
import { errorBgImg } from '../../js/script'
import { imgURL } from '../Home'
// import { EventContentType } from '../../@types/home'
import { format } from 'date-fns'
import { useAppContext } from '../../components/provider/AppProvider'
import { useMyContext } from '../../components/provider/MyProvider'
import { useCommonContext } from '../../components/provider/CommonProvider'
import { getFestivalsList } from '../../api/login'

interface ExhibitionItem {
  id: number // 이벤트 번호 또는 ID
  label: string // 이벤트 제목 또는 라벨
  urlPath: string // URL 경로
  startDate: string // 시작 날짜 (ISO 형식)
  endDate: string // 종료 날짜 (ISO 형식)
  pcImageUrl: string // PC용 이미지 URL
  mobileImageUrl: string // 모바일용 이미지 URL
  displayPeriodType: 'REGULAR' | 'PERIOD' // 표시 기간 유형
  type: 'exhibition' | 'festival' | 'brand' // 이벤트 유형
}

export default function ExhibitionList() {
  const [items, setItems] = useState<ExhibitionItem[]>([])
  // const [page, setPage] = useState<number>(1)
  const [tab, setTab] = useState<string>('전체')

  const { isAppView } = useAppContext()
  const { setToastMessage, setIsToast, setToastEvent } = useMyContext()
  const { setIsLottie } = useCommonContext()

  // const obsRef = useRef<HTMLDivElement>(null) //observer Element
  const preventRef = useRef<boolean>(true) //옵저버 중복 실행 방지
  const endRef = useRef<boolean>(false) //모든 글 로드 확인
  const itemRef = useRef<HTMLDivElement>(null)

  const navigate = useNavigate()

  const REGULAR_END_DATE = '2099-12-31'

  // useEffect(() => {
  //   //옵저버 생성
  //   if (items) {
  //     const observer = new IntersectionObserver(obsHandler, { threshold: 0.5 })
  //     if (obsRef.current) observer.observe(obsRef.current)
  //     return () => {
  //       observer.disconnect()
  //     }
  //   }
  // }, [items, itemRef])

  const getDisplayPeriodType = (endDate: string): 'REGULAR' | 'PERIOD' => {
    const parsedEndDate = parseISO(format(new Date(endDate), 'yyyy-MM-dd'))
    const regularEndDate = parseISO(REGULAR_END_DATE)

    return isAfter(parsedEndDate, regularEndDate) || parsedEndDate.getTime() === regularEndDate.getTime()
      ? 'REGULAR'
      : 'PERIOD'
  }

  const sortItems = (items: ExhibitionItem[]): ExhibitionItem[] => {
    return items
      .sort((a, b) => {
        const aEndDate = new Date(a.endDate).getTime()
        const bEndDate = new Date(b.endDate).getTime()
        const aStartDate = new Date(a.startDate).getTime()
        const bStartDate = new Date(b.startDate).getTime()

        const aIsRegular = a.displayPeriodType === 'REGULAR'
        const bIsRegular = b.displayPeriodType === 'REGULAR'

        // NOTE: 상시 운영끼리는 시작일이 오래된 순서대로 정렬(사업팀 요구 사항)
        if (aIsRegular && bIsRegular) return aStartDate - bStartDate

        // 상시 운영은 뒤로 정렬
        if (aIsRegular) return 1
        if (bIsRegular) return -1

        // 나머지는 종료일이 가까운 순서대로 정렬
        return aEndDate - bEndDate
      })
      .map((item) => ({
        ...item,
        startDate: format(new Date(item.startDate), 'yyyy-MM-dd HH:mm:ss'),
        endDate: format(new Date(item.endDate), 'yyyy-MM-dd HH:mm:ss'),
      }))
  }

  const fetchAllData = async () => {
    const res = await getAllEventList('ING')
    const { data } = await getFestivalsList()
    const festivalList = data.festivals

    const combinedItems = [
      ...res.data.map((el: any) => ({
        id: el.eventNo,
        label: el.label,
        urlPath: el.url,
        startDate: el.startYmdt,
        endDate: el.endYmdt,
        pcImageUrl: el.pcImageUrl,
        mobileImageUrl: el.mobileimageUrl,
        displayPeriodType: el.displayPeriodType || getDisplayPeriodType(el.endYmdt),
        type: 'exhibition',
      })),
      ...festivalList.map((el: any) => ({
        id: el.id,
        label: el.title,
        urlPath: el.urlPath,
        startDate: el.startDate,
        endDate: el.endDate,
        pcImageUrl: el.thumbnailImage,
        mobileImageUrl: el.thumbnailImage,
        displayPeriodType: getDisplayPeriodType(el.endDate),
        type: el.type,
      })),
    ]

    return sortItems(combinedItems) // 정렬 적용
  }

  const fetchExhibitionData = async () => {
    const res = await getAllEventList('ING')
    const exhibitionItems = res.data.map((el: any) => ({
      id: el.eventNo,
      label: el.label,
      urlPath: el.url,
      startDate: el.startYmdt,
      endDate: el.endYmdt,
      pcImageUrl: el.pcImageUrl,
      mobileImageUrl: el.mobileimageUrl,
      displayPeriodType: el.displayPeriodType || getDisplayPeriodType(el.endYmdt),
      type: 'exhibition',
    }))

    return sortItems(exhibitionItems) // 정렬 적용
  }

  const fetchBrandData = async () => {
    const { data } = await getFestivalsList()
    const brandItems = data.festivals
      .filter((el: any) => el.type === 'brand')
      .map((el: any) => ({
        id: el.id,
        label: el.title,
        urlPath: el.urlPath,
        startDate: el.startDate,
        endDate: el.endDate,
        pcImageUrl: el.thumbnailImage,
        mobileImageUrl: el.thumbnailImage,
        displayPeriodType: getDisplayPeriodType(el.endDate),
        type: 'brand',
      }))

    return sortItems(brandItems) // 정렬 적용
  }

  const fetchFestivalData = async () => {
    const { data } = await getFestivalsList()
    const festivalItems = data.festivals
      .filter((el: any) => el.type === 'festival')
      .map((el: any) => ({
        id: el.id,
        label: el.title,
        urlPath: el.urlPath,
        startDate: el.startDate,
        endDate: el.endDate,
        pcImageUrl: el.thumbnailImage,
        mobileImageUrl: el.thumbnailImage,
        displayPeriodType: getDisplayPeriodType(el.endDate),
        type: 'festival',
      }))

    return sortItems(festivalItems) // 정렬 적용
  }

  const exhibitionClickHandler = (item: ExhibitionItem) => {
    const today = format(new Date(), 'yyyy-MM-dd') // 오늘 날짜를 yyyy-MM-dd 형식으로 포맷

    const path = item.type === 'exhibition' ? `/exhibitiondetail/${item.id}` : `/festivals/${item.urlPath}`

    if (isBefore(new Date(item.endDate), new Date(today))) {
      // 종료된 기획전/이벤트
      setIsToast(true)
      setToastMessage('종료된 기획전/이벤트입니다.')
      setToastEvent(() => () => {
        if (isAppView) {
          RNPostMessage(AFWebLinkType.goBack)
        } else {
          navigate(`${process.env.REACT_APP_SHOP_URL}` || '/')
        }
      })
    } else {
      if (isAppView) {
        RNPostMessage(AFWebLinkType.moveToLink, {
          url: `aboutfishing://aboutfishing.com/?page=webview&url=${process.env.REACT_APP_SHOP_URL}${path}`,
        })
      } else {
        navigate(path)
      }
    }
  }

  useEffect(() => {
    setIsLottie(true)
  }, [tab])

  useEffect(() => {
    setItems([])
    endRef.current = false
  }, [tab])

  useEffect(() => {
    preventRef.current = true

    const fetchData = async () => {
      try {
        setIsLottie(true)

        if (tab === '전체') {
          const allData = await fetchAllData()
          setItems(allData)
        } else if (tab === '기획전') {
          const exhibitionData = await fetchExhibitionData()
          setItems(exhibitionData)
        } else if (tab === '브랜드') {
          const brandData = await fetchBrandData()
          setItems(brandData)
        } else if (tab === '이벤트') {
          const festivalData = await fetchFestivalData()
          setItems(festivalData)
        }
      } catch (e) {
        console.error(e)
      } finally {
        setIsLottie(false)
      }
    }

    fetchData()
  }, [tab])

  // const obsHandler = (entries: any) => {
  //   //옵저버 콜백함수
  //   const target = entries[0]
  //   if (!endRef.current && target.isIntersecting && preventRef.current) {
  //     //옵저버 중복 실행 방지
  //     preventRef.current = false //옵저버 중복 실행 방지
  //     setPage((prev) => prev + 1) //페이지 값 증가
  //   }
  // }

  // const getPost = useCallback(async () => {
  //   try {
  //     const res = await getAllEventSearch('ING', page)

  //     const { data } = await getFestivalsList()
  //     const festivalList = data.festivals

  //     console.log('res.data.contents >>> ', res.data.contents)
  //     console.log('festivalList >>> ', festivalList)

  //     // 필터링과 정렬
  //     const sortedItems = res.data.contents
  //       .filter((el: any) => Number(el.eventNo) !== 43284)
  //       .sort((a: any, b: any) => b.eventNo - a.eventNo)

  //     setItem((prev: any) => [...prev, ...sortedItems])
  //     setIsLottie(false)

  //     // 마지막 페이지 확인
  //     if (res.data.totalPage <= page) {
  //       endRef.current = true
  //     }
  //   } catch (e) {
  //     console.error(e)
  //   }
  // }, [page])

  // const getEndPost = useCallback(async () => {
  //   try {
  //     const res = await getAllEventSearch('END', page)

  //     // 정렬
  //     const sortedItems = res.data.contents.sort((a: any, b: any) => b.eventNo - a.eventNo)

  //     setItems((prev: any) => [...prev, ...sortedItems])
  //     setIsLottie(false)

  //     // 마지막 페이지 확인
  //     if (res.data.totalPage <= page) {
  //       endRef.current = true
  //     }
  //   } catch (e) {
  //     console.error(e)
  //   }
  // }, [page])

  return (
    <>
      {isMobile ? (
        <div className='main_wrap'>
          <div className='contents_wrap'>
            <PageTitle className='center'>기획전/이벤트</PageTitle>
          </div>
          <div className='exhibition_list'>
            <div className='tab_wrap'>
              <div className='tab_container'>
                <div
                  className={tab === '전체' ? 'tab on' : 'tab'}
                  onClick={() => {
                    setTab('전체')
                  }}
                >
                  전체
                </div>
                <div
                  className={tab === '기획전' ? 'tab on' : 'tab'}
                  onClick={() => {
                    setTab('기획전')
                  }}
                >
                  기획전
                </div>
                <div
                  className={tab === '브랜드' ? 'tab on' : 'tab'}
                  onClick={() => {
                    setTab('브랜드')
                  }}
                >
                  브랜드
                </div>
                <div
                  className={tab === '이벤트' ? 'tab on' : 'tab'}
                  onClick={() => {
                    setTab('이벤트')
                  }}
                >
                  이벤트
                </div>
              </div>
            </div>
            <div className='contents_wrap'>
              {items.length !== 0 ? (
                <div className='exhibition_list_cont'>
                  {items.map((item: any, index: number) => {
                    return (
                      <div ref={itemRef} key={`exhibition-event-${index}`}>
                        <div className='item' onClick={() => exhibitionClickHandler(item)}>
                          <img
                            src='../../images/exhibition_bg.png'
                            style={{
                              backgroundImage: `url(${
                                item ? (isMobile ? item.mobileImageUrl : item.pcImageUrl) : errorBgImg
                              })`,
                              backgroundSize: item ? `cover` : `36px 36px`,
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat',
                            }}
                            alt=''
                          />
                          <div>
                            <p>{item.label}</p>
                            {item.displayPeriodType === 'REGULAR' ||
                            format(new Date(item.endDate), 'yyyy. M. d') === '2099. 12. 31' ? (
                              <span>상시 운영</span>
                            ) : (
                              <span className='date'>
                                {((): string => {
                                  // 기획전/이벤트 시작일
                                  const startDate = format(new Date(item.startDate), 'yyyy. M. d')
                                  // 기획전/이벤트 종료일
                                  const endDate = format(new Date(item.endDate), 'yyyy. M. d')
                                  return `${startDate} - ${endDate}`
                                })()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className='no_data'>
                  <img src={`${imgURL}/empty_logo.png`} alt='' />
                  {tab === '진행' ? '진행 중' : '종료'} 기획전/이벤트가 없습니다.
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        // pc
        <div className='main_wrap'>
          <div className='pc_content_wrap'>
            <PageTitle className='center'>기획전/이벤트</PageTitle>
          </div>
          <div className='exhibition_list'>
            <div className='tab_wrap'>
              <div className='tab_container'>
                <div
                  className={tab === '전체' ? 'tab on' : 'tab'}
                  onClick={() => {
                    setTab('전체')
                  }}
                >
                  전체
                </div>
                <div
                  className={tab === '기획전' ? 'tab on' : 'tab'}
                  onClick={() => {
                    setTab('기획전')
                  }}
                >
                  기획전
                </div>
                <div
                  className={tab === '브랜드' ? 'tab on' : 'tab'}
                  onClick={() => {
                    setTab('브랜드')
                  }}
                >
                  브랜드
                </div>
                <div
                  className={tab === '이벤트' ? 'tab on' : 'tab'}
                  onClick={() => {
                    setTab('이벤트')
                  }}
                >
                  이벤트
                </div>
              </div>
            </div>
            <div className='contents_wrap'>
              {items.length !== 0 ? (
                <div className='exhibition_list_cont'>
                  {items.map((item: ExhibitionItem, index: number) => (
                    <div key={`exhibition-event-${index}`} ref={itemRef}>
                      <div className='item' onClick={() => exhibitionClickHandler(item)}>
                        <img
                          src='../../images/exhibition_bg.png'
                          style={{
                            backgroundImage: `url(${isMobile ? item.mobileImageUrl : item.pcImageUrl})`,
                          }}
                          alt=''
                        />
                        <div>
                          <p>{item.label}</p>
                          {item.displayPeriodType === 'REGULAR' ||
                          format(new Date(item.endDate), 'yyyy. M. d') === '2099. 12. 31' ? (
                            <span>상시 운영</span>
                          ) : (
                            <span className='date'>
                              {((): string => {
                                // 기획전/이벤트 시작일
                                const startDate = format(new Date(item.startDate), 'yyyy. M. d')
                                // 기획전/이벤트 종료일
                                const endDate = format(new Date(item.endDate), 'yyyy. M. d')
                                return `${startDate} - ${endDate}`
                              })()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='no_data'>
                  <img src={`${imgURL}/empty_logo.png`} alt='' />
                  {tab === '진행' ? '진행 중' : '종료'} 기획전/이벤트가 없습니다.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* <div ref={obsRef}></div> */}
    </>
  )
}
